// The device's end of the live channel: one WebSocket to the list's Durable
// Object, same origin. On open it says hello with the last sequence this
// device applied and takes what comes back (the whole list, or what it
// missed, then every run). While open, each change made here is sent live.
// A dropped socket comes back after 1, 2, 4 … 30 s (jittered), and at once
// when the network returns or the page is shown again: iOS drops a
// background tab's socket within seconds, so resuming is one hello, and a
// socket that looks open but no longer answers is replaced. When the browser
// says the network has gone (`offline`), the socket is let go at once: it
// can carry nothing, and a socket kept would only be probed and waited on
// when the network returns, instead of replaced straight away.
//
// Once the list has answered the hello, the outbox drains (outbox.ts holds
// its rules): every change made here and not yet acknowledged, oldest first,
// then each new one as it is made. A moment after a snapshot or changes land
// (SEEN_DELAY_MS, so a burst is one message), the device tells the list how
// far it has applied (`seen`), and the list prunes its log behind the
// devices it still serves.
//
// The list closes a socket on purpose with one of two codes, and sync stops
// for good on either: 4001 when the session ended (the app clears the
// device), 4003 when the account is suspended (the app keeps everything and
// says the account is paused). Reconnecting after either would only be
// refused.
//
// Nothing here imports from worker/: the protocol is shared/socket.ts.
import { CLOSE_CODES, SOCKET_PATH, type ClientMessage, type ServerMessage } from '@shared/socket';
import { store as appStore, type Store } from '../store/store';
import { createDrain, jittered } from './outbox';

/** The part of a WebSocket sync uses: the browser's own, or a fake in tests. */
export interface SocketLike {
  readonly readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  addEventListener(type: 'open' | 'error', listener: () => void): void;
  addEventListener(type: 'close', listener: (event: { code?: number }) => void): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
}

interface Events { addEventListener(type: string, listener: () => void): void; removeEventListener(type: string, listener: () => void): void }

export interface SyncOptions {
  /** The app's store by default. */
  store?: Store;
  /** Called once when the server ends the session; sync has stopped by then. */
  onEnded?: () => void;
  /** Called once when the server says the account is suspended; sync has stopped by then, and nothing on the device was touched. */
  onSuspended?: () => void;
  /** Opens a socket; the browser's WebSocket by default. */
  connect?: (url: string) => SocketLike;
  /** Where the app is served from; `location` by default. */
  origin?: { protocol: string; host: string };
  /** Where `online` is heard; `window` by default. */
  window?: Events;
  /** Where `visibilitychange` is heard; `document` by default. */
  document?: Events & { readonly visibilityState: string };
  random?: () => number;
}

const CONNECTING = 0;
const OPEN = 1;
export const BACKOFF_MS = { first: 1000, max: 30_000 } as const;
/** How often an open socket is asked whether it is still there; the list answers without waking. */
export const HEARTBEAT_MS = 25_000;
/** The close code the list's object uses when a session ends (worker/list/close.ts). */
export const SESSION_ENDED = CLOSE_CODES.signedOut;
/** The close code the list's object uses when the account is suspended. */
export const ACCOUNT_SUSPENDED = CLOSE_CODES.suspended;
/** How long an unanswered ping waits before the socket is given up for dead. */
export const PONG_WAIT_MS = 10_000;
/** How long after applying what the list sent the device says how far it has got; a burst in between is one `seen`. */
export const SEEN_DELAY_MS = 2_000;

/** The wait before reconnect attempt `n` (from 0): 1, 2, 4 … 30 s, each between half and all of that, so many devices do not return in step. */
export function backoff(attempt: number, random: () => number = Math.random): number {
  return jittered(attempt, BACKOFF_MS, random);
}

/** Keeps this device's copy of the list live until the returned function is called. */
export function startSync(listId: string, opts: SyncOptions = {}): () => void {
  const store = opts.store ?? appStore;
  const connect = opts.connect ?? ((url: string): SocketLike => new WebSocket(url));
  const win = opts.window ?? (typeof window === 'undefined' ? undefined : window);
  const doc = opts.document ?? (typeof document === 'undefined' ? undefined : document);
  const random = opts.random ?? Math.random;

  let socket: SocketLike | undefined;
  let attempt = 0;
  let stopped = false;
  /** Asked for the whole list after a change this build could not apply; one ask per socket. */
  let askedForAll = false;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let heartbeat: ReturnType<typeof setInterval> | undefined;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  /** A `seen` waiting to go, and the last sequence this socket told the list it has. */
  let seenTimer: ReturnType<typeof setTimeout> | undefined;
  let told = 0;
  /** Messages are handled one at a time, in the order they arrived. */
  let inbox: Promise<void> = Promise.resolve();
  const drain = createDrain(store, { random });

  const url = () => {
    const where = opts.origin ?? location;
    return `${where.protocol === 'https:' ? 'wss' : 'ws'}://${where.host}${SOCKET_PATH(listId)}?device=${encodeURIComponent(store.deviceId)}`;
  };
  const say = (s: SocketLike, msg: ClientMessage) => {
    try {
      s.send(JSON.stringify(msg));
    } catch {
      // Closing; its close event brings the next attempt.
    }
  };
  const quiet = () => {
    clearInterval(heartbeat);
    clearTimeout(watchdog);
    clearTimeout(seenTimer);
    heartbeat = watchdog = seenTimer = undefined;
  };
  /** Asks the list for everything again, once per socket until it arrives: what it sends replaces this device's copy. */
  const askForAll = (s: SocketLike) => {
    if (askedForAll) return;
    askedForAll = true;
    told = 0;
    say(s, { type: 'hello', lastSeq: 0 });
  };
  /** Tells the list, a moment from now, how far this device has applied. */
  const scheduleSeen = (s: SocketLike) => {
    seenTimer ??= setTimeout(() => {
      seenTimer = undefined;
      const seq = store.lastSeq();
      if (socket === s && s.readyState === OPEN && seq > told) {
        told = seq;
        say(s, { type: 'seen', seq });
      }
    }, SEEN_DELAY_MS);
  };

  function open(): void {
    if (stopped || socket) return;
    clearTimeout(retry);
    retry = undefined;
    const s = connect(url());
    socket = s;
    askedForAll = false;
    s.addEventListener('open', () => {
      if (socket !== s) return;
      told = store.lastSeq();
      say(s, { type: 'hello', lastSeq: told });
      // The outbox goes once the hello is answered (handle, below).
      drain.open((change) => say(s, { type: 'change', change }));
      heartbeat = setInterval(probe, HEARTBEAT_MS);
    });
    s.addEventListener('message', (event) => {
      if (socket !== s) return;
      clearTimeout(watchdog);
      watchdog = undefined;
      if (event.data === 'pong') return;
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(event.data)) as ServerMessage;
      } catch {
        return;
      }
      attempt = 0; // the list answered, so the next drop starts the backoff again
      inbox = inbox.then(() => handle(s, msg)).catch(() => undefined);
    });
    s.addEventListener('close', (event) => {
      if (socket !== s) return;
      socket = undefined;
      quiet();
      drain.close();
      // The server closed it because the session ended (signed out elsewhere,
      // or everywhere): reconnecting would only meet 401s.
      if (event.code === SESSION_ENDED) {
        stopped = true;
        opts.onEnded?.();
        return;
      }
      // Suspended: stop, and leave the device exactly as it is.
      if (event.code === ACCOUNT_SUSPENDED) {
        stopped = true;
        opts.onSuspended?.();
        return;
      }
      if (!stopped) retry = setTimeout(open, backoff(attempt++, random));
    });
    s.addEventListener('error', () => {
      // A close event follows.
    });
  }

  async function handle(s: SocketLike, msg: ServerMessage): Promise<void> {
    if (stopped) return;
    switch (msg.type) {
      case 'snapshot':
        await store.takeSnapshot(msg.projects, msg.seq, msg.runs);
        askedForAll = false;
        break;
      case 'changes':
        if (!(await store.applyRemote(msg.changes))) askForAll(s);
        break;
      case 'run':
        store.setRun(msg.run);
        return;
      case 'ack':
        await drain.ack(msg.id);
        return;
      case 'error':
        // Gone from the outbox for good: the list's copy replaces the edit it would not take.
        if ((await drain.refused(msg)) === 'refused' && socket === s) askForAll(s);
        return;
    }
    // A snapshot or changes: the first on a socket is its hello's answer, and the outbox may go.
    if (socket !== s) return;
    drain.answered();
    scheduleSeen(s);
  }

  /** Asks an open socket whether it is still there; no answer in time, and it is replaced. */
  function probe(): void {
    const s = socket;
    if (!s || s.readyState !== OPEN) return;
    try {
      s.send('ping');
    } catch {
      return replace();
    }
    watchdog ??= setTimeout(replace, PONG_WAIT_MS);
  }

  /** A socket that stopped answering: closed, and another opened now. */
  function replace(): void {
    const dead = socket;
    socket = undefined;
    quiet();
    drain.close();
    try {
      dead?.close(4000, 'no answer');
    } catch {
      // already gone
    }
    open();
  }

  /** The network has gone: let the socket go now, and come back on `online` (or after the backoff, should that never come). */
  function offline(): void {
    const gone = socket;
    if (stopped || !gone) return;
    socket = undefined;
    quiet();
    drain.close();
    try {
      gone.close(1000, 'offline');
    } catch {
      // already gone
    }
    clearTimeout(retry);
    retry = setTimeout(open, backoff(attempt++, random));
  }

  /** Back online or back in view: check the socket we have, or open one now rather than after the backoff. */
  function resume(): void {
    if (stopped) return;
    if (socket) probe();
    else open();
  }
  const onVisible = () => {
    if (doc?.visibilityState === 'visible') resume();
  };
  /** Back online: a socket still connecting was begun with no network and will fail, so a fresh one goes now. */
  const onOnline = () => {
    if (!stopped && socket?.readyState === CONNECTING) replace();
    else resume();
  };

  // A change made here goes at once if the socket is open and answered, behind anything older still waiting.
  const offApplied = store.onApplied(() => drain.pump());
  win?.addEventListener('online', onOnline);
  win?.addEventListener('offline', offline);
  doc?.addEventListener('visibilitychange', onVisible);
  void store.ready.then(open);

  return function stop() {
    stopped = true;
    offApplied();
    drain.stop();
    win?.removeEventListener('online', onOnline);
    win?.removeEventListener('offline', offline);
    doc?.removeEventListener('visibilitychange', onVisible);
    clearTimeout(retry);
    quiet();
    const s = socket;
    socket = undefined;
    try {
      s?.close(1000, 'stopped');
    } catch {
      // already gone
    }
  };
}
